import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { EmailService } from "./email.service";
import { IEmailType } from '../shared/models/interfaces/email-type';
import { IEmailTemplate } from "../shared/models/interfaces/email-template";
import { EmailTemplate } from "../shared/models/email-template.model";
import { NgForm } from '@angular/forms';
import { FeedbackService } from "../shared/services/feedback.service";
import { takeUntil, catchError, tap } from 'rxjs/operators';
import { Subject, of, forkJoin } from 'rxjs';

@Component({
  selector: 'app-email-template',
  templateUrl: './email-template.component.html',
  styleUrls: ['./email-template.component.scss'],
  providers: [EmailService, FeedbackService]
})
export class EmailTemplateComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  isLoading: boolean;
  previewMode: boolean;
  emailTypes: IEmailType[];
  emailTemplate: IEmailTemplate;
  emailTypeId: number;
  @ViewChild('f', { static: true }) form: NgForm;

  constructor(
    private emailService: EmailService,
    private feedbackService: FeedbackService) {
  }

  ngOnInit() {
    this.isLoading = true;
    const emailTypesObservable = this.emailService.getEmailTypes();

    forkJoin([emailTypesObservable]).pipe(
      tap((response) => {
        if (response) {
          this.emailTypes = response[0];
          this.emailTypeId = this.emailTypes[0].emailTypeId;
          this.loadEmailTemplate();
        } else {
          this.feedbackService.error('An error occurred loading data');
        }
      }),
      catchError(() => {
        this.feedbackService.error('An error occurred in loading the page');
        return [];
      }),
      tap(() => {
        this.isLoading = false;
      })
    ).subscribe();

  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  emailTypeChanged() {
    this.loadEmailTemplate();
  }

  private loadEmailTemplate() {
    this.isLoading = true;
    this.emailService.getEmailTemplateByEmailTypeId(this.emailTypeId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        if (response) {
          this.emailTemplate = response;
        } else {
          this.emailTemplate = new EmailTemplate(this.emailTypeId);
        }
      },
      error: (error) => {
        this.feedbackService.error(error);
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  isSaveDisabled() {
    return !this.form.valid;
  }

  save() {
    if (!this.emailTemplate.emailTemplateId || this.emailTemplate.emailTemplateId === 0) {
      this.emailService
        .createEmailTemplate(this.emailTemplate)
        .pipe(
          tap((data) => {
            this.emailTemplate = data;
            this.feedbackService.success(`Email template created.`);
          }),
          catchError((error) => {
            this.feedbackService.error(
              `There was a problem saving the Email template.`
            );
            console.error(error);
            return of(null);
          })
        )
        .subscribe();
    } else {
      this.emailService.update(this.emailTemplate).pipe(
        tap((data) => {
          this.emailTemplate = data;
          this.feedbackService.success("Email template updated");
        }),
        catchError((error) => {
          this.feedbackService.error("An error occurred while updating the Email template");
          console.error('Error updating Email template:', error);
          return of(null);
        })
      ).subscribe();
    }
  }

  preview() {
    this.previewMode = true;
  }

  back() {
    this.previewMode = false;
  }

  get emailTypeName(): string {
    if (this.emailTypes && this.emailTypeId) {
      const emailType = this.emailTypes.find(s => s.emailTypeId === this.emailTypeId);
      if (emailType) {
        return emailType.emailTypeName;
      }
    }

    return '';
  }
}
